import { getReferenceString, MedplumClient } from "@medplum/core";
import { BundleEntry, PlanDefinition, Task } from "@medplum/fhirtypes";

import { cloneDeep } from "lodash";
import { v7 as uuidv7 } from "uuid";

import { ChecklistTaskStatus } from "@/constants/checklist";
import { ChecklistCodes, QuiltedHealthUsageContextTypes } from "@/fhir/enums/quilted-health";
import { ChecklistTask } from "@/types/checklist-task";
import {
  getChecklistByTemplateResource,
  getChecklistTaskByTemplateResource,
  parseTasksResultsToChecklistTask,
  sortTasksByOrder,
} from "@/utils/checklist";
import {
  getOrganizationReference,
  getResourcesFromBundle,
  joinReferenceString,
} from "@/utils/medplum";

/**
 * Builds the `_filter` expression for checklist template search: templates in the practice
 * compartment that are either public, or private to the given practitioner.
 *
 * `_compartment` is a reference parameter, so it is compared with `eq`. The `co` operator maps to
 * the `:contains` modifier, which Medplum rejects for reference parameters.
 *
 * @param practiceId - Practice Organization id; falls back to the active organization when omitted
 * @param practitionerId - Practitioner whose private templates should be included; when omitted, only public templates match
 * @returns A FHIR `_filter` expression string
 */
const buildChecklistTemplateFilter = (practiceId?: string, practitionerId?: string): string => {
  const organizationFilter = `_compartment eq ${getOrganizationReference(practiceId)}`;

  if (!practitionerId) {
    return `${organizationFilter} and context eq public`;
  }

  const practitionerReference = joinReferenceString("Practitioner", practitionerId);
  return `${organizationFilter} and (context eq public or (context eq private and _compartment eq ${practitionerReference}))`;
};

export const fetchChecklistTemplates = async (
  medplum: MedplumClient,
  practiceId?: string,
  practitionerId?: string,
  sortKey?: string,
) => {
  const sort = sortKey ?? "_lastUpdated";

  const results = await medplum.searchResources(
    "PlanDefinition",
    {
      "context-type": QuiltedHealthUsageContextTypes.ChecklistTemplates,
      status: "active",
      _filter: buildChecklistTemplateFilter(practiceId, practitionerId),
      _sort: sort,
    },
    { cache: "no-cache" },
  );

  const templates = getResourcesFromBundle("PlanDefinition", results.bundle);

  return templates;
};

export const fetchChecklists = async (medplum: MedplumClient, clientId: string) => {
  const results = await medplum.searchResources(
    "Task",
    {
      subject: joinReferenceString("Patient", clientId),
      status: "in-progress,completed",
      code: `${ChecklistCodes.Checklist}`,
    },
    {
      cache: "no-cache",
    },
  );

  const checklists = getResourcesFromBundle("Task", results.bundle);

  const checkListsTasks: ChecklistTask[] = await Promise.all(
    checklists.map(async (checklist) => {
      const tasksResults = await medplum.searchResources(
        "Task",
        {
          "part-of": getReferenceString(checklist),
          status: "in-progress,completed",
          _count: 50,
        },
        {
          cache: "no-cache",
        },
      );

      const associatedTasks = getResourcesFromBundle("Task", tasksResults.bundle);

      const sortedTasks = sortTasksByOrder(associatedTasks);

      const tasks = sortedTasks.reduce(
        (acc, task) => {
          acc[task.id] = task;
          return acc;
        },
        {} as Record<string, Task>,
      );

      return {
        ...checklist,
        tasks,
      };
    }),
  );

  return checkListsTasks;
};

export const createChecklistByTemplates = async (
  medplum: MedplumClient,
  templates: PlanDefinition[],
  clientId: string,
  practitionerId?: string,
) => {
  const entry: BundleEntry[] = [];

  templates.forEach((template) => {
    const fullUrl = `urn:uuid:${uuidv7()}`;

    const visibility = template.useContext?.[0]?.valueCodeableConcept?.coding?.[0]?.code;

    const checklistEntry = getChecklistByTemplateResource(
      template,
      clientId,
      fullUrl,
      visibility,
      practitionerId,
    );
    entry.push(checklistEntry);

    template.action?.forEach((task, index) => {
      const taskEntry = getChecklistTaskByTemplateResource(
        task.title!,
        clientId,
        fullUrl,
        index,
        visibility,
        practitionerId,
      );
      entry.push(taskEntry);
    });
  });

  const results = await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry,
  });

  const checklistAndTasks = getResourcesFromBundle("Task", results) as Task[];

  const parsedChecklistAndTasks = parseTasksResultsToChecklistTask(checklistAndTasks);

  return parsedChecklistAndTasks;
};

export const deleteChecklistOrTask = async (medplum: MedplumClient, task: Task) => {
  const clonedTask = cloneDeep(task);

  if ("tasks" in clonedTask) {
    delete clonedTask.tasks;
  }

  return await medplum.updateResource({ ...clonedTask, status: ChecklistTaskStatus.Cancelled });
};

export const changeChecklistTasksOrder = async (medplum: MedplumClient, tasks: Task[]) => {
  const bundleEntry: BundleEntry[] = [];

  if (!tasks.length) return [];

  tasks.forEach((task) => {
    bundleEntry.push({
      resource: task,
      request: {
        method: "PUT",
        url: `Task/${task.id}`,
      },
    });
  });

  const results = await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: bundleEntry,
  });

  const updatedTasks = getResourcesFromBundle("Task", results) as Task[];

  return updatedTasks;
};

const DEFAULT_CHECKLIST_TASK_ID = "_new";

export const upsertChecklistTask = async (medplum: MedplumClient, task: Task) => {
  const clonedTask = cloneDeep(task);

  if (clonedTask.id === DEFAULT_CHECKLIST_TASK_ID) {
    delete clonedTask.id;
  }

  const results = await medplum.upsertResource(clonedTask, {
    _id: clonedTask.id ? clonedTask.id : "Task",
  });

  return results;
};

export const updateChecklistTasks = async (medplum: MedplumClient, tasks: Task[]) => {
  const bundleEntry: BundleEntry[] = [];

  tasks.forEach((task) => {
    bundleEntry.push({
      resource: task,
      request: {
        method: "PUT",
        url: `Task/${task.id}`,
      },
    });
  });

  const results = await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: bundleEntry,
  });

  const updatedTasks = getResourcesFromBundle("Task", results) as Task[];

  return updatedTasks;
};
